/**
 * MoedaSelect - Seletor de moeda com presets, moedas customizadas e popup "Outras moedas".
 * Ao adicionar uma moeda customizada, ela é salva no backend (reuso) e selecionada.
 */
import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Coins, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import {
  PRESET_CURRENCIES,
  getAllCurrencies,
  setCustomCurrencies,
  getCustomCurrencies,
} from '../../lib/currencies';

const ADD_NEW = '__add_new__';

// Cache simples entre montagens
let _loaded = false;

export const carregarMoedasCustomizadas = async () => {
  try {
    const res = await api.get('/moedas');
    setCustomCurrencies(res.data || []);
    _loaded = true;
  } catch (e) {
    // silencioso — presets continuam funcionando
  }
};

const MoedaSelect = ({ value = 'BRL', onChange, disabled = false, className = '', testId = 'moeda-select' }) => {
  const [, forceRender] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [novaMoeda, setNovaMoeda] = useState({ code: '', name: '', symbol: '', decimals: 2, locale: 'en-US' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!_loaded) {
      carregarMoedasCustomizadas().then(() => forceRender((n) => n + 1));
    }
  }, []);

  const handleSelect = (v) => {
    if (v === ADD_NEW) {
      setNovaMoeda({ code: '', name: '', symbol: '', decimals: 2, locale: 'en-US' });
      setShowDialog(true);
      return;
    }
    onChange?.(v);
  };

  const salvarNovaMoeda = async () => {
    const code = (novaMoeda.code || '').trim().toUpperCase();
    if (!code || code.length < 2) return toast.error('Informe o código (ex.: GBP)');
    if (!novaMoeda.name.trim()) return toast.error('Informe o nome da moeda');
    if (!novaMoeda.symbol.trim()) return toast.error('Informe o símbolo (ex.: £)');

    setSaving(true);
    try {
      const res = await api.post('/moedas', {
        code,
        name: novaMoeda.name.trim(),
        symbol: novaMoeda.symbol.trim(),
        decimals: Number(novaMoeda.decimals) || 2,
        locale: novaMoeda.locale || 'en-US',
      });
      const nova = res.data;
      // Atualiza registro em memória
      const atuais = getCustomCurrencies().filter((m) => m.code !== nova.code);
      setCustomCurrencies([...atuais, nova]);
      forceRender((n) => n + 1);
      onChange?.(nova.code);
      setShowDialog(false);
      toast.success(`Moeda ${nova.code} adicionada`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar moeda');
    } finally {
      setSaving(false);
    }
  };

  const opcoes = getAllCurrencies();

  return (
    <>
      <Select value={value} onValueChange={handleSelect} disabled={disabled}>
        <SelectTrigger className={className} data-testid={testId}>
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-muted-foreground" />
            <SelectValue placeholder="Moeda" />
          </div>
        </SelectTrigger>
        <SelectContent>
          {PRESET_CURRENCIES.map((m) => (
            <SelectItem key={m.code} value={m.code} data-testid={`moeda-opt-${m.code}`}>
              {m.symbol} {m.code} — {m.name}
            </SelectItem>
          ))}
          {opcoes.filter((m) => !PRESET_CURRENCIES.find((p) => p.code === m.code)).map((m) => (
            <SelectItem key={m.code} value={m.code} data-testid={`moeda-opt-${m.code}`}>
              {m.symbol} {m.code} — {m.name}
            </SelectItem>
          ))}
          <SelectItem value={ADD_NEW} data-testid="moeda-opt-outras">
            <span className="flex items-center gap-1 text-primary">
              <Plus className="h-3 w-3" /> Outras moedas...
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" /> Cadastrar nova moeda
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código ISO</Label>
                <Input
                  placeholder="GBP"
                  maxLength={5}
                  value={novaMoeda.code}
                  onChange={(e) => setNovaMoeda({ ...novaMoeda, code: e.target.value.toUpperCase() })}
                  data-testid="nova-moeda-code"
                />
              </div>
              <div>
                <Label className="text-xs">Símbolo</Label>
                <Input
                  placeholder="£"
                  maxLength={4}
                  value={novaMoeda.symbol}
                  onChange={(e) => setNovaMoeda({ ...novaMoeda, symbol: e.target.value })}
                  data-testid="nova-moeda-symbol"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Nome</Label>
              <Input
                placeholder="Libra Esterlina"
                value={novaMoeda.name}
                onChange={(e) => setNovaMoeda({ ...novaMoeda, name: e.target.value })}
                data-testid="nova-moeda-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Casas decimais</Label>
                <Input
                  type="number"
                  min={0}
                  max={4}
                  value={novaMoeda.decimals}
                  onChange={(e) => setNovaMoeda({ ...novaMoeda, decimals: e.target.value })}
                  data-testid="nova-moeda-decimals"
                />
              </div>
              <div>
                <Label className="text-xs">Locale (formatação)</Label>
                <Input
                  placeholder="en-US"
                  value={novaMoeda.locale}
                  onChange={(e) => setNovaMoeda({ ...novaMoeda, locale: e.target.value })}
                  data-testid="nova-moeda-locale"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              A moeda será salva e ficará disponível para reuso em todo o sistema.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={salvarNovaMoeda} disabled={saving} data-testid="nova-moeda-salvar">
              {saving ? 'Salvando...' : 'Salvar moeda'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MoedaSelect;
